// src/pages/KitPage.tsx

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUIStore } from '@/stores/uiStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSubscriptionLimits } from '@/hooks/useSubscription';
import LinkInBioManagement from '@/components/kit/link-in-bio/LinkInBioManagement';
import LinkInBioBuilder from '@/components/kit/link-in-bio/LinkInBioBuilder';
import LinkInBioAnalytics from '@/components/kit/link-in-bio/LinkInBioAnalytics';
import MicrositeManagement from '@/components/kit/microsite/MicrositeManagement';
import MicrositeBuilder from '@/components/kit/microsite/MicrositeBuilder';
import MicrositeAnalytics from '@/components/kit/microsite/MicrositeAnalytics';
import EpkManagement from '@/components/kit/epk/EpkManagement';
import EpkBuilder from '@/components/kit/epk/EpkBuilder';
import EpkAnalytics from '@/components/kit/epk/EpkAnalytics';
import Spinner from '@/components/common/Spinner';
import UpgradeModal from '@/components/common/UpgradeModal';

/** Mobile Kit menu — shown when the user taps "Kit" on mobile. */
function KitMenu() {
  const navigate = useNavigate();

  return (
    <div className="p-4">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-neutral-500">
        Kit
      </h2>
      <nav className="space-y-1">
        <button
          onClick={() => navigate('/app/kit/link-in-bio')}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300 hover:text-neutral-800"
        >
          <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>
          <div>
            <p className="font-medium">Link-in-Bio</p>
            <p className="text-xs text-neutral-500">Create a customizable link page for your fans</p>
          </div>
          <svg className="ml-auto h-4 w-4 flex-shrink-0 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          onClick={() => navigate('/app/kit/microsite')}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300 hover:text-neutral-800"
        >
          <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
            />
          </svg>
          <div>
            <p className="font-medium">Microsite</p>
            <p className="text-xs text-neutral-500">Build a small website to showcase your music</p>
          </div>
          <svg className="ml-auto h-4 w-4 flex-shrink-0 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          onClick={() => navigate('/app/kit/epk')}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300 hover:text-neutral-800"
        >
          <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
            />
          </svg>
          <div>
            <p className="font-medium">Electronic Press Kit</p>
            <p className="text-xs text-neutral-500">A professional press kit for venues and media</p>
          </div>
          <svg className="ml-auto h-4 w-4 flex-shrink-0 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </nav>
    </div>
  );
}

/**
 * Wrapper that gates analytics access behind a paid subscription.
 * - While subscription data is loading, shows a spinner.
 * - If the user has access, renders the analytics component.
 * - If the user is on the free tier, shows the management page with an upgrade modal.
 *   Dismissing the modal redirects to the management page.
 */
function AnalyticsGate({
  analyticsComponent,
  managementRoute,
}: {
  analyticsComponent: React.ReactNode;
  managementRoute: string;
}) {
  const navigate = useNavigate();
  const { canAccessAnalytics, isSubscriptionLoading } = useSubscriptionLimits();

  if (isSubscriptionLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (canAccessAnalytics) {
    return <>{analyticsComponent}</>;
  }

  // Free tier: show upgrade modal, redirect to management page on dismiss
  return (
    <UpgradeModal
      isOpen={true}
      onClose={() => navigate(managementRoute, { replace: true })}
    />
  );
}

export default function KitPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const setActiveSection = useUIStore((s) => s.setActiveSection);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);

  // Ensure Kit section is active when this page mounts
  useEffect(() => {
    setActiveSection('kit');
  }, [setActiveSection]);

  // Hide sidebar on builder pages to give them full width
  const isBuilder = location.pathname.endsWith('/edit');
  useEffect(() => {
    if (isBuilder) {
      setSidebarOpen(false);
    }
    return () => {
      // Restore sidebar when leaving builder
      if (isBuilder) {
        setSidebarOpen(true);
      }
    };
  }, [isBuilder, setSidebarOpen]);

  // On desktop, auto-redirect /app/kit to /app/kit/link-in-bio
  const isKitRoot = location.pathname === '/app/kit' || location.pathname === '/app/kit/';
  useEffect(() => {
    if (isKitRoot && !isMobile) {
      navigate('/app/kit/link-in-bio', { replace: true });
    }
  }, [isKitRoot, isMobile, navigate]);

  const path = location.pathname;

  // ---- EPK routes ----
  if (path.includes('/epk')) {
    if (path.endsWith('/edit')) {
      return <EpkBuilder />;
    }
    if (path.endsWith('/analytics')) {
      return (
        <AnalyticsGate
          analyticsComponent={<EpkAnalytics />}
          managementRoute="/app/kit/epk"
        />
      );
    }
    return <EpkManagement />;
  }

  // ---- Microsite routes ----
  if (path.includes('/microsite')) {
    if (path.endsWith('/edit')) {
      return <MicrositeBuilder />;
    }
    if (path.endsWith('/analytics')) {
      return (
        <AnalyticsGate
          analyticsComponent={<MicrositeAnalytics />}
          managementRoute="/app/kit/microsite"
        />
      );
    }
    return <MicrositeManagement />;
  }

  // ---- Link-in-Bio routes ----
  if (path.endsWith('/edit')) {
    return <LinkInBioBuilder />;
  }

  if (path.endsWith('/analytics')) {
    return (
      <AnalyticsGate
        analyticsComponent={<LinkInBioAnalytics />}
        managementRoute="/app/kit/link-in-bio"
      />
    );
  }

  if (path.includes('/link-in-bio')) {
    return <LinkInBioManagement />;
  }

  // At /app/kit root — show mobile menu on mobile, desktop will have redirected above
  if (isKitRoot && isMobile) {
    return <KitMenu />;
  }

  // Fallback: management page
  return <LinkInBioManagement />;
}
